import React, { useCallback, useState } from 'react';

import { AlertAnimation } from '@eika/alert-animation';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { WarningCard } from '@eika/card';
import { Combobox } from '@eika/combobox';

import { useBankCountries } from '../../../../fetchHooks/useCountryDetails';
import useTranslation from '../../../../hooks/useTranslation';
import { createCrossBorderReceiver } from '../../../../services/crossBorderReceiverService';
import CenteredModalContent from '../../../shared/CenteredModalContent/CenteredModalContent';
import { CrossBorderPaymentActionType } from '../form/actions';
import createReceiverRequest from '../form/createReceiverRequest';
import useFormContext from '../form/useFormContext';
import { receiverError, validateNewReceiver } from '../form/validate';
import NewCrossBorderReceiverBank from './NewCrossBorderReceiverBank';
import NewCrossBorderReceiverSkeleton from './NewCrossBorderReceiverSkeleton';

interface Props {
    onSuccess: (value: boolean) => void;
}

const CreateNewCrossBorderReceiver: React.FC<Props> = ({ onSuccess }) => {
    const translated = useTranslation();
    const {
        data: bankCountryList,
        error: countryListError,
        isLoading: loadingCountryList,
        refetch: refreshCountryList,
    } = useBankCountries();
    const { handleChange, state } = useFormContext();
    const [chosenCountry, setChosenCountry] = useState(state.country?.name || '');
    const [showCreateButton, setShowCreateButton] = useState(false);
    const [validationErrors, setValidationErrors] = useState<receiverError>();
    const [showCreateSpinner, setShowCreateSpinner] = useState(false);

    const handleSubmit = useCallback(() => {
        const validationErrors = validateNewReceiver(translated, state);
        const noValidationErrors = !(
            validationErrors.receiverName ||
            validationErrors.receiverCountryCode ||
            validationErrors.bankCountryCode ||
            validationErrors.bic ||
            validationErrors.accountNumber ||
            validationErrors.creditorDetails ||
            validationErrors.nationalId
        );

        if (!noValidationErrors) {
            setValidationErrors(validationErrors);
            return;
        }

        const newCrossBorderReceiver = createReceiverRequest(state);

        if (noValidationErrors) {
            setShowCreateSpinner(true);
            createCrossBorderReceiver(newCrossBorderReceiver).finally(() => {
                setShowCreateSpinner(false);
                onSuccess(true);
            });
        }
    }, [onSuccess, state, translated]);

    const handleCountrySelection = (value: string) => {
        setValidationErrors({ bankCountryCode: undefined });
        const country = bankCountryList?.find((country) => country.name.toLowerCase() === value.toLowerCase());
        handleChange({
            type: CrossBorderPaymentActionType.SET_BANK_COUNTRY,
            payload: country?.countryCode,
        });
        setChosenCountry(country?.name ?? value);
    };

    if (countryListError) {
        return (
            <CenteredModalContent>
                <WarningCard
                    className="cross-border-payment__country-warning-card"
                    title={translated.crossBorderPayment.newReceiver.errorCountryTitle}
                    isModal
                    icon={<AlertAnimation />}
                >
                    <p>{translated.crossBorderPayment.newReceiver.errorCountry}</p>
                    <Button
                        className="cross-border-payment__country-warning-retry"
                        onClick={() => refreshCountryList()}
                    >
                        {translated.tryAgain}
                    </Button>
                </WarningCard>
            </CenteredModalContent>
        );
    }

    return (
        <>
            {loadingCountryList && <NewCrossBorderReceiverSkeleton />}
            {!loadingCountryList && bankCountryList && (
                <div className="cross-border-payment__country-search max-desktop-width">
                    <Combobox
                        id="new-receiver-bank-countries"
                        label={translated.crossBorderPayment.newReceiver.bankCountry}
                        placeholder={translated.crossBorderPayment.newReceiver.countryPlaceholder}
                        suggestions={bankCountryList?.map((country) => {
                            return { value: country.name };
                        })}
                        value={chosenCountry}
                        onChange={(value) => handleCountrySelection(value)}
                        onSelect={(value) => handleCountrySelection(value.toString())}
                        error={validationErrors?.bankCountryCode}
                        searchInput
                        hideSearchIcon
                    />
                </div>
            )}
            <NewCrossBorderReceiverBank
                countryCode={state.receiver?.bankCountryCode}
                setValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
                setShowNextButton={setShowCreateButton}
            />
            {showCreateButton && (
                <ButtonWithSpinner
                    className="add-new-receiver-button"
                    buttonType="positive"
                    onClick={handleSubmit}
                    showSpinner={showCreateSpinner}
                >
                    {translated.add}
                </ButtonWithSpinner>
            )}
        </>
    );
};

export default CreateNewCrossBorderReceiver;
